import { itemResultLabel } from './item-result';

import type { OperationFailedPage } from '../schemas/release-operation';

/**
 * The failed-items list of an operation as plain data (ACR-1601): rows that link
 * to the existing flag detail and client Flags tab, cursor paging, and the
 * unrecorded target chunks as one aggregate row that never names a client.
 */

export type FailedItemsPage = OperationFailedPage;

export interface ItemRow {
    readonly kind: 'item';
    readonly id: string;
    readonly flagKey: string;
    readonly flagHref: string;
    readonly tenantId: string | null;
    readonly clientHref: string | null;
    readonly resultLabel: string;
    readonly reason?: string;
    readonly note?: string;
}

export interface TargetsIncompleteRow {
    readonly kind: 'targets-incomplete';
    readonly id: 'targets-incomplete';
    readonly count: number;
    readonly label: string;
}

export type ResultRow = ItemRow | TargetsIncompleteRow;

export function flagHref(flagKey: string): string {
    return `/catalog/flags/${encodeURIComponent(flagKey)}`;
}

export function clientHref(tenantId: string): string {
    return `/clients/${encodeURIComponent(tenantId)}/flags`;
}

function targetsIncompleteLabel(count: number): string {
    const clients = count === 1 ? 'client' : 'clients';
    const its = count === 1 ? 'its' : 'their';
    const them = count === 1 ? 'it' : 'them';
    return `${count.toLocaleString('en-US')} ${clients} could not be listed because ${its} targets were not recorded. Start a new operation for ${them}.`;
}

/** The cursor of the next page, or `undefined` on the last one. */
export function nextFailedCursor(page: FailedItemsPage): string | undefined {
    return page.nextCursor ?? undefined;
}

export function resultRows(pages: readonly FailedItemsPage[]): ResultRow[] {
    const rows: ResultRow[] = [];
    for (const page of pages) {
        for (const item of page.items) {
            if (item.result !== 'failed') continue;
            rows.push({
                kind: 'item',
                id: `${item.flagKey}|${item.tenantId ?? ''}`,
                flagKey: item.flagKey,
                flagHref: flagHref(item.flagKey),
                tenantId: item.tenantId,
                clientHref: item.tenantId === null ? null : clientHref(item.tenantId),
                resultLabel: itemResultLabel(item.result),
                ...(item.reason === undefined ? {} : { reason: item.reason }),
                ...(item.note === undefined ? {} : { note: item.note }),
            });
        }
    }

    // The count is the same on every page; the latest one wins if a retry moved it.
    const count = pages.at(-1)?.targetsIncomplete ?? 0;
    if (count > 0) {
        rows.push({
            kind: 'targets-incomplete',
            id: 'targets-incomplete',
            count,
            label: targetsIncompleteLabel(count),
        });
    }
    return rows;
}
